import {type TaillightsRenderingOptions, taillightsRenderingStyle} from '@github-ui/taillights/rendering-styles'
import blockStyles from '@github-ui/taillights/styles'
import {Taillight} from '@github-ui/taillights/taillight'
import type {TaillightRenderer} from '@github-ui/taillights/types'
import type {TaillightsSourceInteractionProps} from '@github-ui/taillights-symbols'
import type {PreparedBlock, RenderingProfile} from '@github-ui/taillights-worker/document'
import type {SourceChunkLayout} from '@github-ui/taillights-worker/source-chunks'
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {type CSSProperties, memo, type Ref, useMemo} from 'react'

import styles from './TaillightsCodeView.module.css'
import {type GutterContentProps, TaillightsGutterRows} from './TaillightsGutter'

const pendingRendering = {tabSize: 4}

export function sourceRenderingStyle(profile?: RenderingProfile, rendering?: TaillightsRenderingOptions) {
  return taillightsRenderingStyle(
    profile
      ? {
          font: profile.font,
          lineHeight: profile.lineHeight,
          paddingInline: profile.paddingInline,
          tabSize: profile.tabSize,
        }
      : (rendering ?? pendingRendering),
  )
}

export const SourceChunk = memo(function Chunk({
  ref,
  virtualIndex,
  directDomUpdates,
  windowLocal,
  gapBefore,
  layout,
  block,
  renderer,
  safariCSSHighlighting,
  gutter,
  lineHeight,
  tabStopLine,
  onFocusChange,
  onRenderError,
  paintReady,
  fallbackStyle,
  showGutter,
}: {
  ref?: Ref<HTMLDivElement>
  virtualIndex: number
  directDomUpdates: boolean
  windowLocal: boolean
  gapBefore?: number
  layout: SourceChunkLayout
  block?: PreparedBlock
  renderer: TaillightRenderer
  safariCSSHighlighting: boolean
  gutter: GutterContentProps
  lineHeight: number
  tabStopLine: number | null
  onFocusChange: (line: number | null) => void
  onRenderError: TaillightsSourceInteractionProps['onRenderError']
  paintReady: boolean
  fallbackStyle: CSSProperties
  showGutter: boolean
}) {
  const safariCSSPaint = renderer === 'css' && safariCSSHighlighting
  const retained = !directDomUpdates && !windowLocal
  const rows = useMemo(
    () => layout.lineNumbers.map((_, index) => ({index: layout.displayStart + index, top: index * lineHeight})),
    [layout, lineHeight],
  )
  const height = Math.max(1, layout.lineNumbers.length) * lineHeight
  const text = block?.text ?? layout.lineNumbers.map(line => gutter.lines[line] ?? '').join('\n')
  return (
    <div
      ref={ref}
      {...testIdProps('taillights-window-chunk')}
      data-index={virtualIndex}
      data-source-start={layout.anchor}
      className={clsx(styles.WindowChunk, retained && styles.retained)}
      style={{
        containIntrinsicBlockSize: retained ? `auto ${height}px` : undefined,
        height,
        lineHeight: `${lineHeight}px`,
        gridTemplateRows: `repeat(${Math.max(1, layout.lineNumbers.length)}, ${lineHeight}px)`,
        marginBlockStart: gapBefore || undefined,
        transform:
          !block || directDomUpdates || windowLocal ? undefined : `translateY(${layout.displayStart * lineHeight}px)`,
      }}
    >
      {showGutter && (
        <TaillightsGutterRows
          {...gutter}
          rows={rows}
          lineHeight={lineHeight}
          tabStopLine={tabStopLine}
          onFocusChange={onFocusChange}
        />
      )}
      <div
        inert={!safariCSSPaint}
        aria-hidden
        className={styles.ChunkSource}
        style={{gridRow: `1 / span ${Math.max(1, layout.lineNumbers.length)}`}}
      >
        {paintReady && block ? (
          <Taillight
            block={block}
            renderer={renderer}
            inert={!safariCSSPaint}
            isSelectable={safariCSSPaint}
            onRenderError={onRenderError}
          />
        ) : (
          <div className={blockStyles.Block} style={{...fallbackStyle, height}}>
            <pre {...testIdProps('taillights-plain-chunk')} className={blockStyles.Paint}>
              {text}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
})
